import type { Table } from 'apache-arrow'
import type { KronaDatasetValue } from '../../../../Page/OrderManagementPage/Modals/Krona/KronaResponses.ts'
import type { SampleBucket } from '../../../SampleGrouping/OperationTypes.ts'
import { profile } from '../../../../../Repository/Visualization/VizPerf.ts'
import { taxaPathKey } from 'Util/Visualisation/TaxaPathKey.ts'

/**
 * Shape of one bucket after the cube has been filtered + re-aggregated.
 *
 * `values` is bit-faithful to what the legacy backend's per-bucket SQL `GROUP BY` produced —
 * one `KronaDatasetValue` per `(bucket, tax_path)`. The cube is more granular (`sample × tax_path`)
 * but `filterCubeByPathAndGrade` aggregates up to this same shape so downstream transformers
 * (`aggregateTaxaAllBuckets`, the trait join, set-math sinks, …) don't need rewriting.
 */
export type FilteredBucketValues = {
  bucketKey: string
  bucketLabel: string
  /**
   * How many samples the bucket holds. `undefined` means no bucket stood behind the row — a
   * single-bucket lookup that found nothing — and is deliberately not the same as `0`, which is a
   * bucket that genuinely has no samples. Consumers must not turn the first into a zero.
   */
  sampleCount?: number
  values: KronaDatasetValue[]
}

/**
 * Cube-backed equivalent of [filterKronaSnapshotByPathAndGrade] — applies the same
 * `selectedPath` prefix and `gradeThreshold` filters but reads from an Arrow `Table`
 * (one row per `(sample, tax_path)`) instead of the legacy bucket-keyed snapshot.
 *
 * Output shape is bit-faithful to the legacy `FilteredBucketValues[]`, so every transformer
 * built on top — `TaxaAggregation`, `TaxaAggregationSingle`, `TaxaColorMapAtLevel`,
 * `KronaFilteredBuckets`, `TraitColorMapAtLevel`, `TraitStackedBarSeries`, `UpSetSink` —
 * keeps working unchanged.
 *
 * Backend pre-aggregated `(bucket × tax_path)` via SQL `GROUP BY`. The cube is more granular
 * (`sample × tax_path`), so we re-aggregate up to the bucket level here:
 * - `magnitude` = `Σ(reads)` over the rows mapped to the same `(bucket, tax_path)`. When
 *   `countMode === 'UNIQUE'`, each row contributes `1` instead of its `reads` — matches the
 *   server-side `countExpression('1' vs 'SUM(reads.raw_reads)')` in `DatasetSql.kt`.
 * - `grade` = `min(grade)` over the same group. The cube column is already MIN-aggregated
 *   per sample by the server's SQL; this min-of-min handles the cross-sample reduction.
 * - `taxa` strips nulls (`mapNotNull` semantics — matches the legacy backend row mapper
 *   which did `(1..8).mapNotNull { row.get(i)?.toString()?.takeIf { it.isNotBlank() } }`).
 *
 */
export function filterCubeByPathAndGrade(opts: {
  table: Table
  selectedBuckets: SampleBucket[]
  selectedPath: string[]
  gradeThreshold: number
  countMode: 'READS' | 'UNIQUE'
}): FilteredBucketValues[] {
  // Profiled hot-path (see VizPerf): this is the "crunch" half of a zoom. Called once per
  // cube-backed transformer (stacked-bar series, color map, …), so the report's `count` reveals
  // how many times the same filter re-runs per zoom.
  return profile('cube.filter', () => filterCubeImpl(opts), {
    rows: opts.table.numRows,
    pathLen: opts.selectedPath.length,
    buckets: opts.selectedBuckets.length
  })
}

/**
 * Arrow → plain-JS decode of the cube, done ONCE per `Table` and reused across every zoom.
 *
 * Reading an Arrow column via `.get()` isn't free, and the cube is ~50k rows × 8 taxon ranks =
 * hundreds of thousands of accesses. None of that decode depends on the zoom knobs
 * (`selectedPath`/`gradeThreshold`/`countMode`/buckets) — only the prefix/grade *filter* does. So
 * we decode the columns + compacted ladder + its join-key (all row-constant) a single time and
 * let each zoom run a cheap pure-JS pass over these arrays.
 *
 * `taxa[i] === null` marks a row whose taxa cell was null (legacy `if (!taxaRow) continue`);
 * `grade[i]` is `NaN` for a null grade. Parallel arrays keep it compact.
 */
interface DecodedCube {
  n: number
  sampleId: Array<string | null>
  taxa: Array<string[] | null>
  reads: Float64Array
  grade: Float64Array // NaN === null
}

const decodedCubes = new WeakMap<Table, DecodedCube>()

function decodeCube(table: Table): DecodedCube {
  const cached = decodedCubes.get(table)
  if (cached) return cached

  const decoded = profile(
    'cube.decode',
    () => {
      const n = table.numRows
      const sampleIdCol = table.getChild('sampleId')!
      const taxaCol = table.getChild('taxa')!
      const readsCol = table.getChild('reads')!
      const gradeCol = table.getChild('grade')!

      const sampleId: Array<string | null> = new Array(n)
      const taxa: Array<string[] | null> = new Array(n)
      const reads = new Float64Array(n)
      const grade = new Float64Array(n)

      for (let i = 0; i < n; i++) {
        sampleId[i] = sampleIdCol.get(i) as string | null
        const g = gradeCol.get(i) as number | null
        grade[i] = g === null ? NaN : g
        reads[i] = Number(readsCol.get(i) ?? 0)

        const taxaRow = taxaCol.get(i) as { get(j: number): string | null } | null
        if (!taxaRow) {
          taxa[i] = null
          continue
        }
        const row: string[] = []
        for (let j = 0; j < 8; j++) {
          const t = taxaRow.get(j)
          if (t !== null && t !== '') row.push(t)
        }
        taxa[i] = row
      }

      return { n, sampleId, taxa, reads, grade }
    },
    { rows: table.numRows }
  )

  decodedCubes.set(table, decoded)
  return decoded
}

/**
 * Repair a taxa-zoom [path] against a (possibly different-source) cube [table].
 *
 * Blast source / SPIN algorithm switches swap the taxonomy tree: different sources fill different
 * ranks (BOLD leaves `domain` blank, NCBI fills `Eukaryota`), which shifts the compacted-ladder
 * positions the path is matched against — so a path captured under one source can prefix-match
 * nothing under another and the view silently empties. This re-anchors it:
 *
 *  1. If the exact prefix still matches ≥1 row, return [path] unchanged (identity — the hot path;
 *     no repair on ordinary re-fetches/revalidations).
 *  2. Otherwise, deepest-first, find the shallowest surviving taxon: for `k = len-1 … 0`, look for
 *     `path[k]` anywhere in the new tree and re-anchor to the compacted prefix ending at it. When a
 *     name sits behind more than one prefix (null-pattern heterogeneity within a source, or a
 *     homonym), pick the prefix that matches the MOST rows — see [beatsBest] for ties.
 *     KNOWN LIMITATION: a single prefix can't cover both null-patterns at once, so the
 *     minority-pattern rows for that taxon stay outside the view (the price of the compacted model;
 *     the rank-anchored model would close this — deliberately not doing that here).
 *  3. If nothing survives, return `[]` (root).
 *
 * Returns the SAME array reference when unchanged so callers can cheaply detect "no repair needed".
 */
export function repairSelectedPath(table: Table, path: string[]): string[] {
  if (path.length === 0) return path
  const cube = decodeCube(table)

  if (anyRowMatchesPrefix(cube, path)) return path

  for (let k = path.length - 1; k >= 0; k--) {
    const best = mostFrequentPrefixEndingAt(cube, path[k]!)
    if (best) return best
  }
  return []
}

function anyRowMatchesPrefix(cube: DecodedCube, path: string[]): boolean {
  const pathLen = path.length
  for (let i = 0; i < cube.n; i++) {
    const taxa = cube.taxa[i]
    if (taxa === null || taxa.length < pathLen) continue
    let ok = true
    for (let j = 0; j < pathLen; j++) {
      if (taxa[j] !== path[j]) {
        ok = false
        break
      }
    }
    if (ok) return true
  }
  return false
}

type PrefixCandidate = { key: string; prefix: string[]; count: number }

/**
 * Most rows, then shallower (a shallower anchor shows more), then smaller key. Without the tiebreaks
 * the winner is whichever candidate the cube listed first, so row order re-anchors a repaired zoom.
 */
function beatsBest(candidate: PrefixCandidate, best: PrefixCandidate): boolean {
  if (candidate.count !== best.count) return candidate.count > best.count
  if (candidate.prefix.length !== best.prefix.length) return candidate.prefix.length < best.prefix.length
  return candidate.key < best.key
}

/** The compacted prefix ending at [target] that occurs in the most rows, or null if absent. */
function mostFrequentPrefixEndingAt(cube: DecodedCube, target: string): string[] | null {
  const counts = new Map<string, { prefix: string[]; count: number }>()
  for (let i = 0; i < cube.n; i++) {
    const taxa = cube.taxa[i]
    if (taxa === null) continue
    const idx = taxa.indexOf(target)
    if (idx < 0) continue
    const prefix = taxa.slice(0, idx + 1)
    const key = taxaPathKey(prefix)
    const entry = counts.get(key)
    if (entry) entry.count++
    else counts.set(key, { prefix, count: 1 })
  }
  let best: { key: string; prefix: string[]; count: number } | null = null
  for (const [key, entry] of counts) {
    if (best && !beatsBest({ key, ...entry }, best)) continue
    best = { key, ...entry }
  }
  return best?.prefix ?? null
}

function filterCubeImpl(opts: {
  table: Table
  selectedBuckets: SampleBucket[]
  selectedPath: string[]
  gradeThreshold: number
  countMode: 'READS' | 'UNIQUE'
}): FilteredBucketValues[] {
  const { table, selectedBuckets, selectedPath, gradeThreshold, countMode } = opts

  const sampleToBucket = new Map<string, string>()
  for (const bucket of selectedBuckets) {
    for (const s of bucket.samples) sampleToBucket.set(s.id, bucket.key)
  }

  // per-bucket: tax-path-key → { taxa, magnitude, grade }
  const perBucket = new Map<string, Map<string, KronaDatasetValue>>()
  for (const bucket of selectedBuckets) perBucket.set(bucket.key, new Map())

  const cube = decodeCube(table)
  const pathLen = selectedPath.length
  const unique = countMode === 'UNIQUE'

  for (let i = 0; i < cube.n; i++) {
    const sampleId = cube.sampleId[i]
    if (!sampleId) continue
    const bucketKey = sampleToBucket.get(sampleId)
    if (!bucketKey) continue

    const grade = cube.grade[i]!
    if (Number.isNaN(grade) || grade < gradeThreshold) continue

    const taxa = cube.taxa[i]
    if (taxa === null) continue

    // Path prefix filter — matched against the COMPACTED ladder, exactly like the legacy
    // `filterKronaSnapshotByPathAndGrade` (`v.taxa[i] !== selectedPath[i]`) and like the
    // aggregation downstream, which groups by `v.taxa[level]`. Matching the *raw* column
    // (`taxaRow.get(j)`) was wrong: a null/blank rank earlier in the ladder shifts the raw
    // index vs. the compacted one, so any zoom past such a gap silently emptied — and going
    // back didn't restore, because the shallower path entries stayed misaligned too.
    if (taxa.length < pathLen) continue
    let prefixOk = true
    for (let j = 0; j < pathLen; j++) {
      if (taxa[j] !== selectedPath[j]) {
        prefixOk = false
        break
      }
    }
    if (!prefixOk) continue

    const taxaKey = taxaPathKey(taxa)
    const bucketMap = perBucket.get(bucketKey)!

    const value = unique ? 1 : cube.reads[i]!

    const existing = bucketMap.get(taxaKey)
    if (existing) {
      existing.magnitude += value
      if (grade < existing.grade) existing.grade = grade
    } else {
      bucketMap.set(taxaKey, { taxa, magnitude: value, grade })
    }
  }

  return selectedBuckets.map((bucket) => ({
    bucketKey: bucket.key,
    bucketLabel: bucket.label,
    sampleCount: bucket.samples.length,
    values: [...perBucket.get(bucket.key)!.values()]
  }))
}
